/**
 * API Client
 * Kitchly v0.5.0
 *
 * Centralized API access with automatic token refresh.
 */

const API_BASE = process.env.REACT_APP_API_URL || '/api';

let accessToken = localStorage.getItem('kitchly_access_token');
let refreshToken = localStorage.getItem('kitchly_refresh_token');
let currentGroupId = localStorage.getItem('kitchly_group_id');

// --- Token management ---

export function setTokens(access, refresh) {
  accessToken = access;
  refreshToken = refresh;
  if (access) localStorage.setItem('kitchly_access_token', access);
  else localStorage.removeItem('kitchly_access_token');
  if (refresh) localStorage.setItem('kitchly_refresh_token', refresh);
  else localStorage.removeItem('kitchly_refresh_token');
}

export function clearTokens() {
  accessToken = null;
  refreshToken = null;
  localStorage.removeItem('kitchly_access_token');
  localStorage.removeItem('kitchly_refresh_token');
  localStorage.removeItem('kitchly_group_id');
}

export function setGroupId(id) {
  currentGroupId = id;
  if (id) localStorage.setItem('kitchly_group_id', id);
  else localStorage.removeItem('kitchly_group_id');
}

export function getGroupId() {
  return currentGroupId;
}

// --- Core fetch wrapper ---

async function apiFetch(path, options = {}) {
  const url = `${API_BASE}${path}`;
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  if (currentGroupId) {
    headers['X-Group-Id'] = currentGroupId;
  }

  const response = await fetch(url, { ...options, headers });

  // Handle token expiration — try refresh once
  if (response.status === 401 && refreshToken) {
    const body = await response.json().catch(() => ({}));
    if (body.code === 'TOKEN_EXPIRED') {
      const refreshed = await attemptRefresh();
      if (refreshed) {
        // Retry original request with new token
        headers['Authorization'] = `Bearer ${accessToken}`;
        return fetch(url, { ...options, headers });
      }
    }
  }

  return response;
}

async function attemptRefresh() {
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken })
    });

    if (!res.ok) {
      clearTokens();
      window.location.href = '/login';
      return false;
    }

    const data = await res.json();
    accessToken = data.access_token;
    localStorage.setItem('kitchly_access_token', data.access_token);
    return true;
  } catch {
    clearTokens();
    window.location.href = '/login';
    return false;
  }
}

// --- Helper: parse response ---

async function parseResponse(response) {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.error || `Request failed (${response.status})`);
    error.status = response.status;
    error.data = data;
    throw error;
  }
  return data;
}

// --- GET / POST / PUT / DELETE helpers ---

async function get(path) {
  const res = await apiFetch(path);
  return parseResponse(res);
}

async function post(path, body) {
  const res = await apiFetch(path, {
    method: 'POST',
    body: JSON.stringify(body)
  });
  return parseResponse(res);
}

async function put(path, body) {
  const res = await apiFetch(path, {
    method: 'PUT',
    body: JSON.stringify(body)
  });
  return parseResponse(res);
}

async function del(path) {
  const res = await apiFetch(path, { method: 'DELETE' });
  return parseResponse(res);
}

// =============================================================================
// AUTH
// =============================================================================
export const auth = {
  register: (data) => post('/auth/register', data),
  login: (data) => post('/auth/login', data),
  refresh: () => post('/auth/refresh', { refresh_token: refreshToken }),
  me: () => get('/auth/me'),
  changePassword: (data) => put('/auth/password', data),
  forgotPassword: (email) => post('/auth/forgot', { email }),
  resetPassword: (data) => post('/auth/reset', data)
};

// =============================================================================
// GROUPS
// =============================================================================
export const groups = {
  list: () => get('/groups'),
  get: (id) => get(`/groups/${id}`),
  create: (name) => post('/groups', { name }),
  invite: (groupId, email) => post(`/groups/${groupId}/invite`, { email }),
  join: (inviteCode) => post('/groups/join', { invite_code: inviteCode }),
  pendingInvitations: () => get('/groups/invitations/pending'),
  respondInvitation: (id, action) => put(`/groups/invitations/${id}`, { action }),
  removeMember: (groupId, userId) => del(`/groups/${groupId}/members/${userId}`),
  deleteGroup: (id) => del(`/groups/${id}`)
};

// =============================================================================
// LOCATIONS
// =============================================================================
export const locations = {
  types: () => get('/locations/types'),
  list: () => get('/locations'),
  create: (data) => post('/locations', data),
  update: (id, data) => put(`/locations/${id}`, data),
  remove: (id) => del(`/locations/${id}`),
  setupDefaults: () => post('/locations/setup-defaults')
};

// =============================================================================
// INVENTORY
// =============================================================================
export const inventory = {
  list: (locationId) => get(`/inventory${locationId ? `?location_id=${locationId}` : ''}`),
  add: (data) => post('/inventory', data),
  scan: (barcode) => post('/inventory/scan', { barcode }),
  confirmScan: (data) => post('/inventory/scan/confirm', data),
  update: (id, data) => put(`/inventory/${id}`, data),
  consume: (id) => post(`/inventory/${id}/consume`),
  remove: (id) => del(`/inventory/${id}`),
  suggestions: (productId) => get(`/inventory/suggestions/${productId}`)
};

// =============================================================================
// SHOPPING
// =============================================================================
export const shopping = {
  list: () => get('/shopping'),
  addItem: (data) => post('/shopping/items', data),
  check: (id) => put(`/shopping/items/${id}/check`),
  uncheck: (id) => put(`/shopping/items/${id}/uncheck`),
  checkout: () => post('/shopping/checkout'),
  standby: () => get('/shopping/standby'),
  place: (placements) => post('/shopping/place', { placements }),
  removeItem: (id) => del(`/shopping/items/${id}`),
  undoConsume: (data) => post('/shopping/undo-consume', data)
};

// =============================================================================
// ADMIN
// =============================================================================
export const admin = {
  inviteCodes: () => get('/admin/invite-codes'),
  createCode: (data) => post('/admin/invite-codes', data),
  toggleCode: (id, isActive) => put(`/admin/invite-codes/${id}`, { is_active: isActive }),
  stats: () => get('/admin/stats')
};
